import { PropsWithChildren, ReactNode, Suspense, useContext, useMemo } from 'react';
import { DrawerContext, DrawerRoot, DrawerSkeleton } from '@givebutter/primitives/drawer';
import { DialogRouteContext } from 'components/dialog-route/DialogRouteContext';
import {
    DialogRouteProps,
    isOpenFromMatch,
    useDialogRouteProps,
} from 'components/dialog-route/useDialogRouteProps';
import { DrawerContent } from 'components/drawer';
import { ReactQueryErrorBoundary } from 'components/react-query-error-boundary';
import { Route as RoutePrimitive } from 'components/router/Route';

interface DrawerRouteProps extends DialogRouteProps {
    contentClassName?: string;
    overlayClassName?: string;
}
const DrawerRoute = ({ overlayClassName, contentClassName, ...rest }: DrawerRouteProps) => {
    const { path, Component, onClose, component, children, ...routeProps } =
        useDialogRouteProps(rest);

    const value = useMemo(() => ({ goBack: onClose }), [onClose]);

    return (
        <DialogRouteContext.Provider value={value}>
            <RoutePrimitive path={path} {...routeProps}>
                {props => {
                    const isOpen = isOpenFromMatch(props.match, path);

                    return (
                        <DrawerRoot
                            open={isOpen}
                            onOpenChange={open => {
                                if (!open) {
                                    onClose();
                                }
                            }}
                        >
                            <DrawerContent
                                overlayClassName={overlayClassName}
                                className={contentClassName}
                                onOpenAutoFocus={e => {
                                    e.preventDefault();
                                    (e.target as HTMLElement | null)?.focus();
                                }}
                            >
                                <DrawerRouteContent>
                                    {children ? (
                                        (children as ReactNode)
                                    ) : (
                                        <Component {...routeProps} />
                                    )}
                                </DrawerRouteContent>
                            </DrawerContent>
                        </DrawerRoot>
                    );
                }}
            </RoutePrimitive>
        </DialogRouteContext.Provider>
    );
};

interface DrawerRouteContentProps extends PropsWithChildren {}
const DrawerRouteContent = ({ children }: DrawerRouteContentProps) => {
    const { open } = useContext(DrawerContext);

    return (
        <ReactQueryErrorBoundary className="h-full w-full bg-white">
            <Suspense fallback={<DrawerSkeleton />}>{open ? children : null}</Suspense>
        </ReactQueryErrorBoundary>
    );
};

export { DrawerRoute };

export type { DrawerRouteProps };
